
import { useState } from 'react';
import { UserLocation } from './use-location-handling';

export interface RefundFormValues {
  merchant: string;
  issue: string;
  amount: string;
  currency: string | null;
  orderNumber: string;
  messageFormat?: "email" | "chat";
}

export interface RefundFormErrors {
  [key: string]: string;
  merchant?: string;
  issue?: string;
  amount?: string;
}

export const useRefundForm = (initialUserLocation: UserLocation) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [values, setValues] = useState<RefundFormValues>({
    merchant: '',
    issue: '',
    amount: '',
    currency: initialUserLocation.currency || 'USD',
    orderNumber: '',
    messageFormat: 'email'
  });
  const [errors, setErrors] = useState<RefundFormErrors>({});

  const validateStep1 = (): boolean => {
    const newErrors: RefundFormErrors = {};
    let isValid = true;

    if (!values.merchant || values.merchant.length < 2) {
      newErrors.merchant = 'Seller name must be at least 2 characters';
      isValid = false;
    }

    if (!values.issue || values.issue.length < 10) {
      newErrors.issue = 'Please provide a more detailed description (at least 10 characters)';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateStep2 = (): boolean => {
    const newErrors: RefundFormErrors = {};
    let isValid = true;

    if (!values.amount) {
      newErrors.amount = 'Amount is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (field: keyof RefundFormValues, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateForm = (): boolean => {
    if (currentStep === 1) {
      return validateStep1();
    } else if (currentStep === 2) {
      return validateStep2();
    }
    return true;
  };

  return {
    currentStep,
    values,
    errors,
    handleChange,
    nextStep,
    prevStep,
    validateForm,
    setValues
  };
};
